import { useState, useEffect } from 'react';
import { ProjectSettings } from 'shared/types/projectSettings/ProjectSettings';
import SuccessIcon from '@/components/customercenter-ui/icons/checkCircle';
import { useCheckout } from '@/context/checkoutProvider';
import { Button } from '@/customercenter-ui/ui/button';
import { maskPhoneNumber, maskPostalCode } from '@/customercenter-ui/utils/formatUtils';
import { useProduct } from '@/frontastic';
import { useProjSettings } from '@/frontastic/hooks/useProjectSettings/useProjectSettings';
import { GlobalPageTexts } from '@/helpers/i18n';
import { mapLocaleToMeaningfulFormat } from '@/helpers/utils/i18n';
import { TranslateText } from '@/helpers/utils/siteUtils';
import { GetEstimatedDeliveryDatePayload } from '@/sdk/composable-commerce/types/payloads/GetEstimatedDeliveryDatePayload';
import { DEFAULT_SITE_LANG } from '../../../../constants/constant';
import GuestShipping from '../../checkout/guestShipping';
import { formatArrivalDate } from '../../components/utils/formatUtils';
import { addZipToCookie, getArrivalDateForShippingMethod, getShippingMethodDisplayName } from '../../utils/fedexUtils';

interface ShippingAddressProps {
  color: string;
  edit: boolean;
  address: any;
  cartData: any;
  availableShippingMethods: any;
}

const ShippingAddress: React.FC<ShippingAddressProps> = ({ color, address, cartData, availableShippingMethods }) => {
  const Texts = TranslateText(GlobalPageTexts, DEFAULT_SITE_LANG);
  const { getEstimatedDeliveryDate } = useProduct();
  const [fedexResponse, setFedexResponse] = useState();
  const [signatureRequired, setSignatureRequired] = useState(false);
  const { shippingMethod, setCartDeliveryDate, lineItemsAvailibiltyMsgs, shippingBackorderDate, checkoutExtraDetails } =
    useCheckout();
  const projectSettings: ProjectSettings = useProjSettings();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [editShippingForm, setEditShippingForm] = useState<boolean>(false);
  const { goToCheckoutStep } = useCheckout();

  useEffect(() => {
    if (lineItemsAvailibiltyMsgs?.length > 0) {
      const { modifiedDate, actualArrivalDate, originalEstimatedDeliveryDate, modifiedStandardDate } =
        getArrivalDateForShippingMethod(
          fedexResponse,
          getShippingMethodDisplayName(availableShippingMethods, shippingMethod ?? ''),
          projectSettings?.defaultDeliveryDates,
          projectSettings,
          cartData?.containsOnlyDropShipItems ?? cartData?.containsOnlyDropShipAndPersonalizedItems,
          cartData?.containsOnlyPersonalizedItems,
          shippingBackorderDate ?? '',
        );

      const currentSelectedShippingMethod = availableShippingMethods?.find(
        (method: any) => method?.shippingMethodId === shippingMethod,
      );

      if (currentSelectedShippingMethod) {
        const shippingInfo = {
          shippingMethodId: currentSelectedShippingMethod?.shippingMethodId,
          date: modifiedDate,
          actualArrivalDate,
          standardDeliveryDate: modifiedStandardDate,
          name: currentSelectedShippingMethod?.displayName,
          originalEstimatedDeliveryDate,
        };
        setCartDeliveryDate(JSON.stringify(shippingInfo));
        localStorage.setItem('cartItemsDate', JSON.stringify(shippingInfo));
      }
    }
  }, [address, fedexResponse, lineItemsAvailibiltyMsgs]);

  useEffect(() => {
    if (!address) return;
    let response: any;
    const timeoutId = setTimeout(() => {
      const fetchArrivalDate = async () => {
        try {
          const postalCode = address?.postalCode.substring(0, 5);
          const payload: GetEstimatedDeliveryDatePayload = {
            countryCode: address?.country,
            pageName: 'CHECKOUT',
            zipCode: postalCode,
          };
          const response: any = await getEstimatedDeliveryDate(payload);
          if (!response?.isError) {
            setFedexResponse(response);
            addZipToCookie(maskPostalCode(address?.postalCode));
          }
        } catch (error) {
          response.isError = true;
          setFedexResponse(response);
        }
      };

      fetchArrivalDate();
    }, 2000);

    return () => clearTimeout(timeoutId);
  }, [address]);

  useEffect(() => {
    if (!checkoutExtraDetails) return;
    if (checkoutExtraDetails?.signatureRequired) {
      setSignatureRequired(checkoutExtraDetails?.signatureRequired);
    }
  }, [checkoutExtraDetails]);

  return (
    <>
      {editShippingForm ? (
        <GuestShipping
          data={address}
          onPaymentPage={true}
          cartData={cartData}
          shippingMethods={availableShippingMethods}
        />
      ) : (
        <section className="border-t border-foggyGray px-4 lg:rounded-md lg:border lg:shadow-md lg:shadow-foggyGray">
          <section className="flex items-start gap-x-[12px] lg:gap-x-[12px]">
            <section className="pt-6 lg:pt-6">
              <SuccessIcon className={`fill-[ h-9 w-9${color}]`} />
            </section>
            <article className="mr-auto">
              <h1 className="pt-7 text-[22px] font-bold leading-7 text-forestGreen">{Texts?.shipping}</h1>
            </article>
            {!editShippingForm ? (
              <Button
                onClick={() => {
                  goToCheckoutStep(1);
                }}
                className="bg-white pr-6 pt-8  text-base font-normal leading-5 text-blueLight underline"
              >
                {Texts?.edit}
              </Button>
            ) : null}
          </section>
          <section className={`grid pt-6 ${cartData?.giftNotes ? 'grid-cols-3' : 'grid-cols-2'}`}>
            <section className="pb-8 pl-12 lg:pb-9">
              <strong className="pt-7 text-lg font-bold leading-6">{Texts?.shippingAddress}</strong>
              <section className="gbmask overflow-hidden text-ellipsis pr-24 pt-2.5 text-base font-normal lg:pr-3">
                <p>
                  {address?.firstName} {address?.lastName}
                </p>
                <p>
                  {address?.streetNumber} {address?.streetName}
                </p>
                <p>{address?.additionalAddressInfo}</p>
                <p>
                  {address?.city}, {address?.state} {maskPostalCode(address?.postalCode)}
                </p>
                <p className="mt-3">{maskPhoneNumber(address?.phone)}</p>
              </section>
            </section>
            <article className="pb-4 pl-11 lg:pb-0">
              <strong className="pt-8 text-lg font-bold leading-6  lg:pt-7">{Texts?.shippingMethods}</strong>
              {getArrivalDateForShippingMethod(
                fedexResponse,
                getShippingMethodDisplayName(availableShippingMethods, shippingMethod),
                projectSettings?.defaultDeliveryDates,
                projectSettings,
                cartData?.containsOnlyDropShipItems ?? cartData?.containsOnlyDropShipAndPersonalizedItems,
                cartData?.containsOnlyPersonalizedItems,
                shippingBackorderDate ?? '',
              ) && (
                <>
                  <p className="overflow-hidden text-ellipsis pr-6 pt-2.5 text-base font-normal lg:pb-0 lg:pr-3">
                    {Texts?.arrivesBy}{' '}
                    <strong>
                      <strong>
                        {formatArrivalDate(
                          getArrivalDateForShippingMethod(
                            fedexResponse,
                            getShippingMethodDisplayName(availableShippingMethods, shippingMethod),
                            projectSettings?.defaultDeliveryDates,
                            projectSettings,
                            cartData?.containsOnlyDropShipItems ?? cartData?.containsOnlyDropShipAndPersonalizedItems,
                            cartData?.containsOnlyPersonalizedItems,
                            shippingBackorderDate ?? '',
                          ).modifiedDate,
                          mapLocaleToMeaningfulFormat(),
                        )}
                      </strong>{' '}
                    </strong>{' '}
                  </p>
                  <p>{getShippingMethodDisplayName(availableShippingMethods, shippingMethod)}</p>
                  {signatureRequired && <p className="mt-5 font-bold"> Signature Required</p>}
                </>
              )}
            </article>
            {cartData?.giftNotes && (
              <section className="pr-22">
                <p className="pb-2.5 text-lg font-bold">{mapLocaleToMeaningfulFormat().giftMessageLabel}</p>
                <p className="gbmask pb-7">{cartData?.giftNotes}</p>
              </section>
            )}
          </section>
        </section>
      )}
    </>
  );
};

export default ShippingAddress;
